import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe } from '../api/auth';
import Sidebar from '../components/Sidebar';
import CollectingOverlay from '../components/CollectingOverlay';

const API_BASE = process.env.REACT_APP_API_URL || '';

function SectorAnalysis() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 데이터 상태
  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [sectorStocks, setSectorStocks] = useState([]);
  const [leadingStocks, setLeadingStocks] = useState([]);
  const [dataDate, setDataDate] = useState('');

  // 필터
  const [topSectors, setTopSectors] = useState(5);
  const [minRs, setMinRs] = useState(80);

  // 탭
  const [activeTab, setActiveTab] = useState('sectors'); // sectors, leading

  useEffect(() => {
    const init = async () => {
      try {
        const userData = await getMe();
        setUser(userData);
      } catch (err) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  // 전체 섹터 조회
  const fetchSectors = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/sector/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSectors(data.sectors || []);
      setDataDate(data.date || '');
    } catch (err) {
      console.error('섹터 조회 실패:', err);
    }
  }, []);

  // 섹터 내 종목 조회
  const fetchSectorStocks = useCallback(async (sectorCode) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/sector/${sectorCode}/stocks?min_rs=0&limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSectorStocks(data.stocks || []);
      setSelectedSector({
        sector_code: sectorCode,
        sector_name: data.sector_name,
        avg_rs_rating: data.avg_rs_rating,
        sector_rank: data.sector_rank
      });
    } catch (err) {
      console.error('섹터 종목 조회 실패:', err);
    }
  }, []);

  // 주도주 조회
  const fetchLeadingStocks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/sector/leading-stocks?top_sectors=${topSectors}&min_rs=${minRs}&limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLeadingStocks(data.stocks || []);
      setDataDate(data.date || '');
    } catch (err) {
      console.error('주도주 조회 실패:', err);
    }
  }, [topSectors, minRs]);

  // 초기 데이터 로드
  useEffect(() => {
    if (!loading) {
      fetchSectors();
    }
  }, [loading, fetchSectors]);

  // 주도주 탭 선택 시 데이터 로드
  useEffect(() => {
    if (activeTab === 'leading' && !loading) {
      fetchLeadingStocks();
    }
  }, [activeTab, loading, fetchLeadingStocks]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  if (loading) {
    return (
      <CollectingOverlay>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </CollectingOverlay>
    );
  }

  return (
  <CollectingOverlay>
    <div className="min-h-screen bg-gray-50">
      <Sidebar user={user} onLogout={handleLogout} />

      <main className="p-4 md:p-8 pt-20 md:pt-24 w-full max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">섹터 분석</h1>
            <p className="text-gray-500 text-sm mt-1">
              기준일: {dataDate || '-'}
            </p>
          </div>
        </div>

        {/* 탭 */}
        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => setActiveTab('sectors')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
              activeTab === 'sectors'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            섹터 순위
          </button>
          <button
            onClick={() => setActiveTab('leading')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
              activeTab === 'leading'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            주도주
          </button>
        </div>

        {/* 섹터 순위 탭 */}
        {activeTab === 'sectors' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 섹터 목록 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <h2 className="text-base font-semibold text-gray-800 mb-4">섹터 순위 (6개월 수익률 기준)</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs border-b border-gray-100">
                      <th className="text-left py-2 font-medium">순위</th>
                      <th className="text-left py-2 font-medium">섹터</th>
                      <th className="text-right py-2 font-medium">RS</th>
                      <th className="text-right py-2 font-medium">6M수익률</th>
                      <th className="text-right py-2 font-medium">종목수</th>
                      <th className="text-right py-2 font-medium">신고가</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectors.map((sector, idx) => (
                      <tr
                        key={sector.sector_code}
                        onClick={() => fetchSectorStocks(sector.sector_code)}
                        className={`border-b border-gray-50 cursor-pointer transition ${
                          selectedSector?.sector_code === sector.sector_code
                            ? 'bg-blue-50'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className={`py-2.5 font-semibold ${
                          idx < 5 ? 'text-green-600' : idx >= sectors.length - 5 ? 'text-red-500' : 'text-gray-700'
                        }`}>
                          {sector.sector_rank}
                        </td>
                        <td className={`py-2.5 ${
                          idx < 5 ? 'text-green-600' : idx >= sectors.length - 5 ? 'text-red-500' : 'text-gray-700'
                        }`}>
                          {sector.sector_name}
                        </td>
                        <td className={`py-2.5 text-right font-semibold ${
                          sector.avg_rs_rating >= 80 ? 'text-green-600' :
                          sector.avg_rs_rating >= 60 ? 'text-blue-600' :
                          sector.avg_rs_rating >= 40 ? 'text-gray-700' : 'text-red-500'
                        }`}>{sector.avg_rs_rating?.toFixed(0)}</td>
                        <td className={`py-2.5 text-right ${
                          sector.avg_change_rate >= 0 ? 'text-red-500' : 'text-blue-500'
                        }`}>
                          {sector.avg_change_rate >= 0 ? '+' : ''}{sector.avg_change_rate?.toFixed(1)}%
                        </td>
                        <td className="py-2.5 text-right text-gray-500">{sector.stock_count}</td>
                        <td className="py-2.5 text-right">
                          <span className="text-gray-700">{sector.new_high_count}</span>
                          <span className="text-gray-400 text-xs ml-1">
                            ({sector.new_high_ratio?.toFixed(1)}%)
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 선택된 섹터의 종목 목록 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <h2 className="text-base font-semibold text-gray-800 mb-4">
                {selectedSector ? (
                  <>
                    {selectedSector.sector_name}
                    <span className="text-gray-500 text-sm font-normal ml-2">
                      (순위 {selectedSector.sector_rank}위, 평균RS {selectedSector.avg_rs_rating?.toFixed(1)})
                    </span>
                  </>
                ) : '섹터를 선택하세요'}
              </h2>
              {selectedSector && (
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="text-gray-500 text-xs border-b border-gray-100">
                        <th className="text-left py-2 font-medium">종목</th>
                        <th className="text-right py-2 font-medium">RS</th>
                        <th className="text-right py-2 font-medium">현재가</th>
                        <th className="text-right py-2 font-medium">신고가比</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectorStocks.map((stock) => (
                        <tr
                          key={stock.stock_code}
                          className="border-b border-gray-50 hover:bg-gray-50"
                        >
                          <td className="py-2.5">
                            <div className={`font-medium ${
                              stock.rs_rating >= 80 ? 'text-green-600' :
                              stock.rs_rating >= 50 ? 'text-gray-700' : 'text-red-500'
                            }`}>{stock.stock_name}</div>
                            <div className="text-gray-400 text-xs">{stock.stock_code}</div>
                          </td>
                          <td className={`py-2.5 text-right font-semibold ${
                            stock.rs_rating >= 80 ? 'text-green-600' :
                            stock.rs_rating >= 50 ? 'text-gray-700' : 'text-red-500'
                          }`}>{stock.rs_rating}</td>
                          <td className="py-2.5 text-right text-gray-700">{stock.close?.toLocaleString()}</td>
                          <td className="py-2.5 text-right">
                            <span className={stock.high_ratio >= 95 ? 'text-amber-600 font-semibold' : 'text-gray-600'}>
                              {stock.high_ratio?.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!selectedSector && (
                <div className="text-center text-gray-400 py-12">
                  왼쪽에서 섹터를 선택하면 해당 섹터의 종목이 표시됩니다.
                </div>
              )}
            </div>
          </div>
        )}

        {/* 주도주 탭 */}
        {activeTab === 'leading' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            {/* 필터 */}
            <div className="flex flex-wrap items-center gap-4 mb-4 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <label className="text-gray-600 text-sm">상위 섹터:</label>
                <select
                  value={topSectors}
                  onChange={(e) => setTopSectors(Number(e.target.value))}
                  className="border border-gray-200 text-gray-700 rounded-lg px-3 py-1.5 text-sm bg-white"
                >
                  <option value={3}>3개</option>
                  <option value={5}>5개</option>
                  <option value={10}>10개</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-gray-600 text-sm">최소 RS:</label>
                <select
                  value={minRs}
                  onChange={(e) => setMinRs(Number(e.target.value))}
                  className="border border-gray-200 text-gray-700 rounded-lg px-3 py-1.5 text-sm bg-white"
                >
                  <option value={90}>90+</option>
                  <option value={80}>80+</option>
                  <option value={70}>70+</option>
                  <option value={50}>50+</option>
                </select>
              </div>
              <button
                onClick={fetchLeadingStocks}
                className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition"
              >
                조회
              </button>
            </div>

            <h2 className="text-base font-semibold text-gray-800 mb-4">
              주도 섹터의 주도주
              <span className="text-gray-500 text-sm font-normal ml-2">
                (상위 {topSectors}개 섹터, RS {minRs}+)
              </span>
            </h2>

            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-gray-500 text-xs border-b border-gray-100">
                    <th className="text-left py-2 font-medium">섹터</th>
                    <th className="text-left py-2 font-medium">종목</th>
                    <th className="text-right py-2 font-medium">RS</th>
                    <th className="text-right py-2 font-medium">현재가</th>
                    <th className="text-right py-2 font-medium">신고가比</th>
                    <th className="text-right py-2 font-medium">52주고가</th>
                  </tr>
                </thead>
                <tbody>
                  {leadingStocks.map((stock) => (
                    <tr
                      key={stock.stock_code}
                      className="border-b border-gray-50 hover:bg-gray-50"
                    >
                      <td className="py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          stock.sector_rank === 1 ? 'bg-amber-100 text-amber-700' :
                          stock.sector_rank === 2 ? 'bg-gray-200 text-gray-600' :
                          stock.sector_rank === 3 ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-500'
                        }`}>
                          {stock.sector_rank}위
                        </span>
                        <span className="ml-2 text-gray-700">{stock.sector_name}</span>
                      </td>
                      <td className="py-2.5">
                        <div className="font-medium text-gray-800">{stock.stock_name}</div>
                        <div className="text-gray-400 text-xs">{stock.stock_code}</div>
                      </td>
                      <td className="py-2.5 text-right">
                        <span className={`font-semibold ${
                          stock.rs_rating >= 90 ? 'text-green-600' : 'text-blue-600'
                        }`}>
                          {stock.rs_rating}
                        </span>
                      </td>
                      <td className="py-2.5 text-right text-gray-700">{stock.close?.toLocaleString()}</td>
                      <td className="py-2.5 text-right">
                        <span className={stock.high_ratio >= 95 ? 'text-amber-600 font-semibold' : 'text-gray-600'}>
                          {stock.high_ratio?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-2.5 text-right text-gray-400">
                        {stock.high_52w?.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {leadingStocks.length === 0 && (
              <div className="text-center text-gray-400 py-12">
                조건에 맞는 종목이 없습니다.
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  </CollectingOverlay>
  );
}

export default SectorAnalysis;
